/**
 * NovaWidget — Orchestrateur agents IA
 * Bouton animé dans le header + dialog modal centrée.
 * State partagé via NovaContext.
 * Exports: NovaProvider, NovaButton, NovaDialog
 */

import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';
import { Sparkles, Network, X, Send, Loader, Plus, AlertCircle } from 'lucide-react';
import { novaChat } from '../api';
import MarkdownContent from './MarkdownContent';

// ─── Context ───────────────────────────────────────────────────────────────────

const NovaContext = createContext(null);

const AGENT_STYLES = {
  cygnus: { label: 'Cygnus', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-400' },
  orion:  { label: 'Orion',  color: 'bg-teal-100 text-teal-700',     dot: 'bg-teal-400'   },
  lyra:   { label: 'Lyra',   color: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-400'  },
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NovaProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'animating'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const currentModule = useRef(null);

  const handleOpen = useCallback(() => {
    if (open) { setOpen(false); return; }
    if (phase !== 'idle') return;
    setPhase('animating');
    const t = setTimeout(() => {
      setOpen(true);
      setPhase('idle');
    }, 650);
    return () => clearTimeout(t);
  }, [open, phase]);

  const handleClose = useCallback(() => setOpen(false), []);
  const newConversation = useCallback(() => setMessages([]), []);

  const sendMessage = useCallback(async (text, module) => {
    if (!text.trim() || sending) return;
    setSending(true);
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: text }]);

    try {
      const res = await novaChat({
        message: text.trim(),
        context: module ? { module } : undefined,
      });
      const data = res.data;
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.message,
          sources: data.sources || [],
          agentId: data.agentId,
          agentName: data.agentName,
        },
      ]);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Nova n\'est pas disponible pour l\'instant.';
      setMessages(prev => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: `⚠️ ${errMsg}`, sources: [], agentId: null },
      ]);
    }
    setSending(false);
  }, [sending]);

  return (
    <NovaContext.Provider value={{
      open, phase, messages, input, setInput, sending,
      handleOpen, handleClose, newConversation, sendMessage,
      currentModule,
    }}>
      {children}
    </NovaContext.Provider>
  );
}

// ─── Bouton header ─────────────────────────────────────────────────────────────

export function NovaButton({ currentView }) {
  const { phase, handleOpen, open } = useContext(NovaContext);
  const animating = phase === 'animating';

  return (
    <button
      onClick={handleOpen}
      title="Nova — Orchestrateur IA"
      className={`flex items-center gap-1.5 rounded-full py-1.5 font-medium text-sm transition-all ${
        open
          ? 'bg-purple-700 text-white shadow-lg px-3'
          : animating
            ? 'bg-purple-500 text-white shadow-md px-3'
            : 'bg-purple-500 hover:bg-purple-600 text-white shadow-sm hover:shadow-md px-3'
      }`}
      style={{ minWidth: animating ? '170px' : 'auto', transition: 'min-width 0.5s ease, background-color 0.2s' }}
    >
      <Sparkles size={15} className="flex-shrink-0" />
      <span className="whitespace-nowrap overflow-hidden transition-all duration-500"
        style={{ maxWidth: animating ? '160px' : '40px', opacity: 1 }}
      >
        {animating ? 'Nova pour vous servir' : 'Nova'}
      </span>
    </button>
  );
}

// ─── Message bubble ────────────────────────────────────────────────────────────

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[78%] bg-purple-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed">
          {message.content}
        </div>
      </div>
    );
  }

  const agentStyle = message.agentId ? AGENT_STYLES[message.agentId] : null;

  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Network size={11} className="text-purple-600" />
        </div>
        <span className="text-xs font-semibold text-purple-700">Nova</span>
        {agentStyle && (
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${agentStyle.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${agentStyle.dot}`} />
            via {agentStyle.label}
          </span>
        )}
      </div>
      <div className="text-sm leading-relaxed">
        <MarkdownContent content={message.content} />
        {message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-100 space-y-0.5">
            {message.sources.map((s, i) => (
              <div key={i} className="text-xs text-gray-400 flex items-center gap-1">
                <span>📄</span>
                <span>{s.documentName || 'Documentation'}</span>
                {s.sectionTitle && <span className="text-gray-300">— {s.sectionTitle}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Dialog ───────────────────────────────────────────────────────────────────

export function NovaDialog({ currentView, userRole }) {
  const { open, handleClose, messages, input, setInput, sending, sendMessage, newConversation, currentModule } = useContext(NovaContext);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Synchronise le module courant dans le ref
  useEffect(() => { currentModule.current = currentView; }, [currentView, currentModule]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = () => {
    if (!input.trim() || sending) return;
    const text = input;
    setInput('');
    sendMessage(text, currentModule.current);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div
        className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-purple-100 flex flex-col"
        style={{
          width: '480px',
          maxWidth: 'calc(100vw - 32px)',
          height: '560px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0 rounded-t-2xl"
          style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}
        >
          <div className="flex items-center gap-2">
            <Network size={16} className="text-white" />
            <div>
              <div className="font-semibold text-white text-sm">Nova</div>
              <div className="text-white/70 text-xs">Orchestrateur — Cygnus · Orion · Lyra</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={newConversation}
              className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition"
              title="Nouvelle conversation"
            >
              <Plus size={15} />
            </button>
            <button
              onClick={handleClose}
              className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition"
              title="Fermer"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 text-xs mt-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mx-auto">
                <Network size={24} className="text-purple-400" />
              </div>
              <p className="font-medium text-gray-600">Bonjour, je suis Nova.</p>
              <p>Je vous oriente vers le bon agent selon votre question :</p>
              <div className="flex justify-center gap-2 flex-wrap mt-2">
                {Object.values(AGENT_STYLES).filter(s => s.label !== 'Orion' || userRole === 'admin').map(s => (
                  <span key={s.label} className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
                    {s.label}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-1.5 mt-4 text-left">
                {[
                  'Comment ouvrir un lot de traçabilité ?',
                  'Quel est le protocole de nettoyage HACCP ?',
                  'Comment calculer les charges sociales ?',
                ].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => { setInput(suggestion); inputRef.current?.focus(); }}
                    className="text-left px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded-lg text-xs text-purple-700 transition"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, idx) => (
            <MessageBubble key={msg.id || idx} message={msg} />
          ))}
          {sending && (
            <div className="mb-5">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
                  <Network size={11} className="text-purple-600" />
                </div>
                <span className="text-xs font-semibold text-purple-700">Nova</span>
              </div>
              <Loader size={14} className="animate-spin text-purple-400" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-100 px-4 py-3 flex-shrink-0">
          <div className="flex items-end gap-2 mb-2">
            <textarea
              ref={inputRef}
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
              rows={1}
              placeholder="Posez votre question…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{ maxHeight: '100px', overflowY: 'auto' }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="flex-shrink-0 w-9 h-9 rounded-xl text-white flex items-center justify-center disabled:opacity-40 transition"
              style={{ backgroundColor: '#9333ea' }}
            >
              <Send size={15} />
            </button>
          </div>
          {/* Disclaimer */}
          <div className="flex items-start gap-1.5 text-xs text-gray-400">
            <AlertCircle size={12} className="flex-shrink-0 mt-0.5 text-gray-300" />
            <span>Nova est un outil d'assistance IA. Ses réponses sont indicatives — votre jugement prime toujours.</span>
          </div>
        </div>
      </div>
    </>
  );
}
